import { motion } from "framer-motion";
import { useState } from "react";

const MissionRocketIcon = ({ className, isActive }) => (
  <svg viewBox="0 0 512 512" className={className} fill="none" aria-hidden="true">
    <defs>
      <linearGradient
        id="mission-rocket-gradient"
        x1="56"
        y1="396"
        x2="456"
        y2="116"
        gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#0b3f7a" />
        <stop offset="0.52" stopColor="#1182b7" />
        <stop offset="1" stopColor="#1ec4e4" />
      </linearGradient>
      <filter id="rocket-flame-glow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="4" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    <motion.g
      animate={isActive ? { y: [-6, 6, -6] } : { y: 0 }}
      transition={
        isActive ? { duration: 2.5, ease: "easeInOut", repeat: Infinity } : { duration: 0.3 }
      }>
      <motion.path
        d="M216 416 L256 490 L296 416 Z"
        fill="#38bdf8"
        filter="url(#rocket-flame-glow)"
        animate={
          isActive ? { scaleY: [0.7, 1.4, 0.7], opacity: [0.6, 1, 0.6] } : { scaleY: 0, opacity: 0 }
        }
        style={{ originX: 0.5, originY: 0.8 }}
        transition={
          isActive ? { duration: 0.4, ease: "easeInOut", repeat: Infinity } : { duration: 0.2 }
        }
      />
      <path
        d="M166 300 L96 416 L176 396"
        stroke="url(#mission-rocket-gradient)"
        strokeWidth="24"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M346 300 L416 416 L336 396"
        stroke="url(#mission-rocket-gradient)"
        strokeWidth="24"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M256 56 C256 56 156 176 156 316 C156 376 256 416 256 416 C256 416 356 376 356 316 C356 176 256 56 256 56 Z"
        stroke="url(#mission-rocket-gradient)"
        strokeWidth="24"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="256" cy="216" r="44" stroke="url(#mission-rocket-gradient)" strokeWidth="24" />
      <circle cx="256" cy="216" r="16" fill="#1caed5" />
      <path
        d="M216 416 L296 416"
        stroke="url(#mission-rocket-gradient)"
        strokeWidth="24"
        strokeLinecap="round"
      />
    </motion.g>

    <motion.line
      x1="120"
      y1="80"
      x2="120"
      y2="140"
      stroke="#7dd3fc"
      strokeWidth="6"
      strokeLinecap="round"
      animate={isActive ? { y: [0, 400], opacity: [0, 1, 0] } : { opacity: 0 }}
      transition={
        isActive ? { duration: 0.8, ease: "linear", repeat: Infinity, delay: 0.1 } : { duration: 0 }
      }
    />
    <motion.line
      x1="392"
      y1="120"
      x2="392"
      y2="180"
      stroke="#67e8f9"
      strokeWidth="6"
      strokeLinecap="round"
      animate={isActive ? { y: [-50, 350], opacity: [0, 1, 0] } : { opacity: 0 }}
      transition={
        isActive ? { duration: 1, ease: "linear", repeat: Infinity, delay: 0.4 } : { duration: 0 }
      }
    />
    <motion.line
      x1="80"
      y1="240"
      x2="80"
      y2="280"
      stroke="#38bdf8"
      strokeWidth="6"
      strokeLinecap="round"
      animate={isActive ? { y: [0, 300], opacity: [0, 1, 0] } : { opacity: 0 }}
      transition={
        isActive ? { duration: 0.6, ease: "linear", repeat: Infinity, delay: 0.6 } : { duration: 0 }
      }
    />
  </svg>
);

const VisionTechEyeIcon = ({ className, isActive }) => (
  <svg viewBox="0 0 512 512" className={className} fill="none" aria-hidden="true">
    <defs>
      <linearGradient
        id="vision-tech-eye-gradient"
        x1="56"
        y1="396"
        x2="456"
        y2="116"
        gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#0b3f7a" />
        <stop offset="0.52" stopColor="#1182b7" />
        <stop offset="1" stopColor="#1ec4e4" />
      </linearGradient>
      <filter id="vision-signal-glow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="3.2" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
    <path
      d="M24 256C84 166 168 116 256 116C344 116 428 166 488 256C428 346 344 396 256 396C168 396 84 346 24 256Z"
      stroke="url(#vision-tech-eye-gradient)"
      strokeWidth="28"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <motion.g
      animate={
        isActive ? { scaleY: [1, 0.06, 1], opacity: [1, 0.25, 1] } : { scaleY: 1, opacity: 1 }
      }
      transition={{ duration: 1.1, ease: [0.25, 0.1, 0.25, 1], times: [0, 0.5, 1] }}
      style={{ originX: 0.5, originY: 0.5 }}>
      <circle cx="256" cy="256" r="92" stroke="url(#vision-tech-eye-gradient)" strokeWidth="24" />
      <circle cx="256" cy="256" r="34" fill="#0b5d91" />
      <path
        d="M72 256H148L178 226"
        stroke="#0f4f8f"
        strokeWidth="20"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="96" cy="256" r="20" fill="#0f4f8f" />
      <path
        d="M440 256H364L334 286"
        stroke="#1caed5"
        strokeWidth="20"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="416" cy="256" r="20" fill="#1caed5" />
    </motion.g>
    <motion.circle
      r="8"
      fill="#7dd3fc"
      filter="url(#vision-signal-glow)"
      animate={
        isActive
          ? {
              cx: [96, 148, 178, 220, 256],
              cy: [256, 256, 226, 240, 256],
              opacity: [0, 1, 1, 0.9, 0],
              scale: [0.7, 1, 1, 0.9, 0.5],
            }
          : { opacity: 0, scale: 0.5, cx: 96, cy: 256 }
      }
      transition={
        isActive
          ? { duration: 1.6, ease: "easeInOut", repeat: Infinity, delay: 1.12 }
          : { duration: 0.2 }
      }
      style={{ originX: 0.5, originY: 0.5 }}
    />
    <motion.circle
      r="8"
      fill="#67e8f9"
      filter="url(#vision-signal-glow)"
      animate={
        isActive
          ? {
              cx: [416, 364, 334, 292, 256],
              cy: [288, 256, 286, 270, 256],
              opacity: [0, 1, 1, 0.9, 0],
              scale: [0.7, 1, 1, 0.9, 0.5],
            }
          : { opacity: 0, scale: 0.5, cx: 416, cy: 288 }
      }
      transition={
        isActive
          ? { duration: 1.6, ease: "easeInOut", repeat: Infinity, delay: 1.4 }
          : { duration: 0.2 }
      }
      style={{ originX: 0.5, originY: 0.5 }}
    />
    <motion.circle
      cx="256"
      cy="256"
      r="18"
      fill="#38bdf8"
      filter="url(#vision-signal-glow)"
      animate={
        isActive
          ? { opacity: [0.12, 0.55, 0.12], scale: [0.8, 1.2, 0.8] }
          : { opacity: 0.1, scale: 0.8 }
      }
      transition={
        isActive
          ? { duration: 1.6, ease: "easeInOut", repeat: Infinity, delay: 1.12 }
          : { duration: 0.2 }
      }
      style={{ originX: 0.5, originY: 0.5 }}
    />
    <motion.path
      d="M24 256C84 166 168 116 256 116C344 116 428 166 488 256L488 40L24 40Z"
      animate={
        isActive
          ? {
              d: [
                "M24 256C84 166 168 116 256 116C344 116 428 166 488 256L488 40L24 40Z",
                "M24 256C84 256 168 256 256 256C344 256 428 256 488 256L488 40L24 40Z",
                "M24 256C84 166 168 116 256 116C344 116 428 166 488 256L488 40L24 40Z",
              ],
            }
          : { d: "M24 256C84 166 168 116 256 116C344 116 428 166 488 256L488 40L24 40Z" }
      }
      transition={{ duration: 1.1, ease: [0.25, 0.1, 0.25, 1], times: [0, 0.5, 1] }}
      fill="white"
    />
    <motion.path
      d="M24 256C84 346 168 396 256 396C344 396 428 346 488 256L488 472L24 472Z"
      animate={
        isActive
          ? {
              d: [
                "M24 256C84 346 168 396 256 396C344 396 428 346 488 256L488 472L24 472Z",
                "M24 256C84 256 168 256 256 256C344 256 428 256 488 256L488 472L24 472Z",
                "M24 256C84 346 168 396 256 396C344 396 428 346 488 256L488 472L24 472Z",
              ],
            }
          : { d: "M24 256C84 346 168 396 256 396C344 396 428 346 488 256L488 472L24 472Z" }
      }
      transition={{ duration: 1.1, ease: [0.25, 0.1, 0.25, 1], times: [0, 0.5, 1] }}
      fill="white"
    />
    <motion.path
      d="M24 256C84 166 168 116 256 116C344 116 428 166 488 256"
      animate={
        isActive
          ? {
              d: [
                "M24 256C84 166 168 116 256 116C344 116 428 166 488 256",
                "M24 256C84 256 168 256 256 256C344 256 428 256 488 256",
                "M24 256C84 166 168 116 256 116C344 116 428 166 488 256",
              ],
            }
          : { d: "M24 256C84 166 168 116 256 116C344 116 428 166 488 256" }
      }
      transition={{ duration: 1.1, ease: [0.25, 0.1, 0.25, 1], times: [0, 0.5, 1] }}
      stroke="url(#vision-tech-eye-gradient)"
      strokeWidth="18"
      strokeLinecap="round"
    />
    <motion.path
      d="M24 256C84 346 168 396 256 396C344 396 428 346 488 256"
      animate={
        isActive
          ? {
              d: [
                "M24 256C84 346 168 396 256 396C344 396 428 346 488 256",
                "M24 256C84 256 168 256 256 256C344 256 428 256 488 256",
                "M24 256C84 346 168 396 256 396C344 396 428 346 488 256",
              ],
            }
          : { d: "M24 256C84 346 168 396 256 396C344 396 428 346 488 256" }
      }
      transition={{ duration: 1.1, ease: [0.25, 0.1, 0.25, 1], times: [0, 0.5, 1] }}
      stroke="url(#vision-tech-eye-gradient)"
      strokeWidth="18"
      strokeLinecap="round"
    />
  </svg>
);

const CardUnderline = ({ isActive, colorHex }) => (
  <svg className="mt-2 h-4 w-40" viewBox="0 0 100 20" fill="none" preserveAspectRatio="none">
    <motion.path
      d="M 0 10 H 40 L 50 3 H 85"
      stroke={colorHex}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      initial={{ pathLength: 0.4, opacity: 0.5 }}
      animate={{ pathLength: isActive ? 1 : 0.4, opacity: isActive ? 1 : 0.5 }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
    />
    <motion.circle
      cx="85"
      cy="3"
      r="2.5"
      fill={colorHex}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: isActive ? 1 : 0, scale: isActive ? 1 : 0 }}
      transition={{ duration: 0.3, delay: isActive ? 0.35 : 0 }}
    />
  </svg>
);

const LiquidEtherBackground = () => {
  return (
    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden bg-[#f8fafc]">
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          x: ["0%", "10%", "0%"],
          y: ["0%", "5%", "0%"],
          borderRadius: [
            "40% 60% 70% 30% / 40% 40% 60% 60%",
            "60% 40% 30% 70% / 60% 60% 40% 40%",
            "40% 60% 70% 30% / 40% 40% 60% 60%",
          ],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="absolute -top-[20%] -left-[10%] h-[80vw] w-[80vw] opacity-30"
        style={{ background: "radial-gradient(circle, #38bdf8 0%, transparent 70%)" }}
      />
      <motion.div
        animate={{
          scale: [1.2, 1, 1.2],
          x: ["0%", "-10%", "0%"],
          y: ["0%", "-5%", "0%"],
          borderRadius: [
            "60% 40% 30% 70% / 60% 60% 40% 40%",
            "40% 60% 70% 30% / 40% 40% 60% 60%",
            "60% 40% 30% 70% / 60% 60% 40% 40%",
          ],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        className="absolute -right-[10%] -bottom-[20%] h-[80vw] w-[80vw] opacity-20"
        style={{ background: "radial-gradient(circle, #10b981 0%, transparent 70%)" }}
      />
      <motion.div
        animate={{ backgroundPosition: ["0px 0px", "60px 104px"] }}
        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='104' viewBox='0 0 60 104' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 104L0 86.6025V17.3975L30 0l30 17.3975v69.205L30 104zM2 85.4474l28 16.165 28-16.165V18.5526L30 2.3875 2 18.5526v66.8948z' fill='%230b3f7a' fill-opacity='0.4'/%3E%3C/svg%3E")`,
          backgroundSize: "60px 104px",
          maskImage: "radial-gradient(ellipse at center, black 20%, transparent 80%)",
        }}
      />
    </div>
  );
};

const MissionVision = () => {
  const [activeCard, setActiveCard] = useState(null);

  const cards = [
    {
      id: "mission",
      title: "Mission",
      ring: "bg-gradient-to-b from-[#9ecc48] via-sky-400 to-blue-600",
      accentTitle: "text-[#0b3f7a]",
      underlineHex: "#2563eb",
      iconShell: "border-slate-100 bg-white",
      iconColor: "",
      iconSize: "h-36 w-36 md:h-44 md:w-44",
      shineOnHover: true,
      spinColor1: "#9ecc48",
      spinColor2: "#1182b7",
      Icon: MissionRocketIcon,
      description:
        "To continuously build and enhance Vet Assist into a complete and comprehensive system that covers the full business process of veterinary clinics, allowing the system to handle operational complexity so veterinarians can focus on patient care.\n\nTo simplify and streamline clinic operations by providing a reliable platform that manages appointments, records, billing, inventory, and daily workflows in one integrated system.\n\nTo deliver a solution that creates real value by improving efficiency, reducing manual work, and supporting long term clinic growth.\n\nTo provide premium service and continuous innovation, ensuring that every clinic benefits from a system that evolves with their needs.\n\nTo foster a strong and committed team at Endsofttech, dedicated to building dependable solutions and supporting clinics with consistency and excellence.",
    },
    {
      id: "vision",
      title: "Vision",
      ring: "bg-gradient-to-l from-[#9ecc48] via-teal-300 to-blue-500",
      accentTitle: "text-[#10b981]",
      underlineHex: "#059669",
      iconShell: "border-slate-100 bg-white",
      iconColor: "",
      iconSize: "h-36 w-36 md:h-44 md:w-44",
      shineOnHover: false,
      spinColor1: "#10b981",
      spinColor2: "#1ec4e4",
      Icon: VisionTechEyeIcon,
      description:
        "To be the most trusted provider of premium veterinary software in the Philippines, delivering reliable and comprehensive systems that allow clinics to focus on what matters most, quality patient care.",
    },
  ];

  return (
    <section className="relative z-10 overflow-hidden py-24 md:py-32">
      <LiquidEtherBackground />
      <div className="relative z-10 mx-auto max-w-6xl px-6 md:px-12">
        <div className="mb-16 flex flex-col items-center text-center md:mb-32">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-6 inline-block rounded-full border border-blue-100 bg-white/80 px-5 py-2 shadow-sm backdrop-blur-sm">
            <span className="bg-linear-to-r from-[#1182b7] to-[#10b981] bg-clip-text text-xs font-bold tracking-[0.2em] text-transparent uppercase">
              Our Purpose
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 pb-6 text-4xl font-black tracking-tight md:text-5xl lg:text-6xl">
            <span className="text-[#0b3f7a]">Mission</span>
            <span className="font-light text-slate-300">&</span>
            <span className="relative inline-block">
              <span className="text-[#10b981]">Vision</span>
              <svg
                className="pointer-events-none absolute -bottom-6 left-0 h-10 w-[140%] overflow-visible"
                viewBox="0 0 160 30"
                preserveAspectRatio="none">
                <defs>
                  <filter id="echo-glow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="2" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                <motion.path
                  d="M 0 15 H 30 L 35 5 L 45 25 L 50 15 H 120"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  filter="url(#echo-glow)"
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.5, ease: "easeInOut" }}
                />
                <motion.circle
                  cx="120"
                  cy="15"
                  r="4"
                  fill="#10b981"
                  filter="url(#echo-glow)"
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 1.5 }}
                />
                <motion.circle
                  cx="120"
                  cy="15"
                  r="12"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="1"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: [0, 0.6, 0], scale: [0.5, 2.5] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 1.5 }}
                />
                <motion.circle
                  cx="120"
                  cy="15"
                  r="12"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="0.5"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: [0, 0.4, 0], scale: [0.5, 4] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 2.5 }}
                />
              </svg>
            </span>
          </motion.h2>
        </div>

        <div className="mx-auto flex w-full max-w-5xl flex-col items-center gap-14">
          {cards.map((card) => {
            const Icon = card.Icon;
            const isActive = activeCard === card.id;
            return (
              <motion.article
                key={card.id}
                tabIndex={0}
                onHoverStart={() => {
                  if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
                    setActiveCard(card.id);
                  }
                }}
                onHoverEnd={() => {
                  if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
                    setActiveCard(null);
                  }
                }}
                onFocus={() => {
                  if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
                    setActiveCard(card.id);
                  }
                }}
                onBlur={() => {
                  if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
                    setActiveCard(null);
                  }
                }}
                onClick={() => {
                  if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
                    setActiveCard(activeCard === card.id ? null : card.id);
                  }
                }}
                className="group w-full cursor-pointer outline-none">
                <motion.div
                  animate={{ borderRadius: isActive ? "24px" : "32px" }}
                  transition={{ duration: 0.4 }}
                  className="relative flex w-full overflow-hidden p-0.5 shadow-[0_20px_50px_rgb(11,63,122,0.06)] transition-shadow duration-500 hover:shadow-[0_25px_60px_rgb(11,63,122,0.12)]">
                  <div className="absolute top-1/2 left-1/2 aspect-square w-[250%] -translate-x-1/2 -translate-y-1/2 opacity-70 transition-opacity duration-500 group-hover:opacity-100">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                      className="h-full w-full"
                      style={{
                        background: `conic-gradient(from 0deg, transparent 20%, ${card.spinColor1} 50%, transparent 80%, ${card.spinColor2} 100%)`,
                      }}
                    />
                  </div>
                  <motion.div
                    animate={{ borderRadius: isActive ? "22px" : "30px" }}
                    transition={{ duration: 0.4 }}
                    className="relative z-10 flex w-full flex-col overflow-hidden bg-white/90 backdrop-blur-2xl md:flex-row">
                    <div className="pointer-events-none absolute top-1/2 right-0 h-full w-1/2 -translate-y-1/2 overflow-hidden opacity-[0.02]">
                      <Icon className="h-[150%] w-full translate-x-1/4" isActive={false} />
                    </div>
                    <div className="relative flex w-full shrink-0 items-center justify-center border-b border-slate-100/50 p-8 md:w-[35%] md:border-r md:border-b-0 md:p-12">
                      <div
                        className={`absolute h-[85%] w-[85%] rounded-full ${card.ring} transition-all duration-700 ease-out ${isActive ? "scale-105 opacity-80" : "scale-100 opacity-60"}`}
                      />
                      <div
                        className={`relative z-10 inline-flex items-center justify-center overflow-hidden rounded-full border ${card.iconShell} bg-white shadow-lg transition-transform duration-500 ${isActive ? "scale-105" : "scale-100"} ${card.iconSize}`}>
                        <Icon className={`h-3/5 w-3/5 ${card.iconColor}`} isActive={isActive} />
                      </div>
                    </div>
                    <div className="relative flex w-full flex-col justify-center px-8 py-10 md:w-[65%] md:px-14 md:py-16">
                      <div className="flex w-full items-center justify-between">
                        <div>
                          <h3
                            className={`text-3xl font-black tracking-tight uppercase ${card.accentTitle}`}>
                            {card.title}
                          </h3>
                          <CardUnderline isActive={isActive} colorHex={card.underlineHex} />
                        </div>
                        <motion.div
                          animate={{
                            rotate: isActive ? 180 : 0,
                            backgroundColor: isActive ? "#f0f9ff" : "transparent",
                          }}
                          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-400">
                          <svg
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </motion.div>
                      </div>
                      <motion.div
                        initial={false}
                        animate={{
                          height: isActive ? "auto" : 0,
                          opacity: isActive ? 1 : 0,
                          marginTop: isActive ? 24 : 0,
                        }}
                        transition={{ duration: 0.5 }}
                        className="overflow-hidden">
                        <p className="relative z-20 pr-4 text-sm leading-relaxed whitespace-pre-line text-slate-600 md:text-base">
                          {card.description}
                        </p>
                      </motion.div>
                    </div>
                  </motion.div>
                </motion.div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MissionVision;
